import { HttpStatusCode } from '@angular/common/http';
import { AfterViewInit, Component } from '@angular/core';
import { catchError, Observable, of, switchMap, finalize, BehaviorSubject, combineLatest } from 'rxjs';
import { GetClinicalFeedbackOrdersRequest, ProblemDetails } from '@app/api-module/control-centre-api-client';
import { SnackbarService } from '../services/snackbar.sevice';
import { CommonUtilities } from '../shared/common-utilities';
import { ClinicalFeedbackConstants } from './clinical-feedback-constants';
import { SearchOrderResponse } from './clinical-feedback.model';
import { ClinicalFeedbackService } from './clinical-feedback.service';
import { OrgSiteSelectionService } from '@app/shared/org-site-selection/org-site-selection.service';
import { AuthenticationService } from '@app/authentication/services/authentication.service';
import { RoleEnum } from '@app/api-module/user-management-api-client-v2';

@Component({
    selector: 'app-clinical-feedback',
    templateUrl: './clinical-feedback.component.html'
})
export class ClinicalFeedbackComponent implements AfterViewInit {
    constructor(
        private readonly snackbarService: SnackbarService,
        private readonly clinicalFeedbackService: ClinicalFeedbackService,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly authenticationService: AuthenticationService
    ) {}

    private readonly searchSubmissionSource = new BehaviorSubject<GetClinicalFeedbackOrdersRequest>(null);
    private readonly refreshTrigger = new BehaviorSubject(null);
    studyItemsDataSource$!: Observable<SearchOrderResponse[]>;
    searchOrderOverLimit = false;
    loading = false;
    searchApplied = false;

    ngAfterViewInit() {
        this.studyItemsDataSource$ = combineLatest([
            this.orgSiteSelectionService.getOrgSelected$(),
            this.orgSiteSelectionService.getSitesSelected(),
            this.searchSubmissionSource,
            this.refreshTrigger
        ]).pipe(
            switchMap(([orgSelected, sitesSelected, searchSubmission]) => {
                if (searchSubmission) {
                    return this.handleServiceCall(this.clinicalFeedbackService.searchStudy(searchSubmission));
                } else if (orgSelected && sitesSelected?.length > 0) {
                    let siteIds = sitesSelected;
                    if (
                        this.currentUserHasOrgWideAccess() &&
                        (siteIds.length > 1 || this.orgSiteSelectionService.getHasOnlyOneSiteFlag() || siteIds[0] === null)
                    ) {
                        siteIds = [];
                    }

                    return this.handleServiceCall(this.clinicalFeedbackService.getExistingClinicalFeedback(orgSelected.orgCode, siteIds));
                }
            })
        );
    }

    private handleServiceCall(call: Observable<SearchOrderResponse[]>): Observable<SearchOrderResponse[]> {
        this.loading = true;
        return call.pipe(
            catchError(err => {
                if (err.status === HttpStatusCode.BadRequest) {
                    if (
                        err instanceof ProblemDetails &&
                        err.detail &&
                        err.detail === ClinicalFeedbackConstants.SEARCH_ORDER_OVER_LIMIT_MESSAGE
                    ) {
                        this.searchOrderOverLimit = true;
                    } else {
                        const errMessage = CommonUtilities.displayErrorMessage(err, ClinicalFeedbackConstants.CANNOT_SEARCH_STUDY_MESSAGE);
                        this.snackbarService.openSnackBar(errMessage ?? '', SnackbarService.SNACK_BAR_CLASS_ALERT);
                    }
                }
                return of([]);
            }),
            finalize(() => {
                this.loading = false;
            })
        );
    }

    onSearchSubmit(event: GetClinicalFeedbackOrdersRequest) {
        this.searchSubmissionSource.next(event);
        this.searchApplied = true;
        this.searchOrderOverLimit = false;
    }

    onSearchClear() {
        this.searchSubmissionSource.next(null);
        this.searchOrderOverLimit = false;
        this.searchApplied = false;
    }

    refreshFeedbackTable() {
        this.refreshTrigger.next(null);
    }

    currentUserHasOrgWideAccess() {
        const currentUser = this.authenticationService.getCurrentUser();
        const allowedSites = currentUser.getAllowedSites(
            this.orgSiteSelectionService.getOrgSelectedValue()?.orgCode,
            RoleEnum.ClinicalFeedback
        );

        return allowedSites?.length === 0;
    }
}
